/**
 * The change model behind the Changes pages — the four stages, two approvals
 * process (docs-company ADR-201 Amendment 2) as data, the states the change
 * page shows (ADR-218 §2.3.2), and the shapes of GET /changes
 * (specs/changes/change-page/openapi.yaml).
 *
 * Every word a page prints about a change's progress comes from here: the four
 * stage names, the three sizes and their rules, the five states, the four
 * parts of the brief. Nothing is stored twice: SystemDox reads a state from
 * GitHub at sync time, and a stage is derived from those states, never typed in.
 */

/** Shape → Build → Release → Learn (ADR-201 Amendment 2, point 1). */
export const STAGES = ['shape', 'build', 'release', 'learn'] as const;
export type Stage = (typeof STAGES)[number];

export const STAGE_LABEL: Record<Stage, string> = {
  shape: 'Shape',
  build: 'Build',
  release: 'Release',
  learn: 'Learn',
};

export const STAGE_WHAT: Record<Stage, string> = {
  shape: 'One agent assembles the brief: the decision, the contract, the behaviour and the plan. Spikes are free and never merge.',
  build: 'One story per session, one pull request per repository per story. The merge into preprod is the build.',
  release: 'The release pull request, read from the release view: the scenarios now @built, the contract now served, what a customer will see.',
  learn: 'Every merge is recorded, the decision is registered, the documents are watched. A defect becomes a rule, a check or a skill line in the same week.',
};

/** The two approvals (point 1): the brief at the end of Shape, then the release. */
export const APPROVALS = {
  one: { label: 'Approval one', what: 'the brief', how: 'The owner merges the brief’s pull requests, in one sitting. The merge is the approval.' },
  two: { label: 'Approval two', what: 'the release', how: 'The owner’s approving review of the release pull request. A new push dismisses it.' },
} as const;

/** The size of the change decides the parts of the brief (point 5). */
export const SIZES = ['small', 'ordinary', 'foundational'] as const;
export type ChangeSize = (typeof SIZES)[number];

export const SIZE_RULE: Record<ChangeSize, string> = {
  small: 'A new field, a new rule inside an existing capability: the brief is the contract and the scenarios, no decision record, and approval one is one merge.',
  ordinary: 'All four parts of the brief, read and approved in one sitting.',
  foundational: 'A boundary moves, or every product is affected: the decision is approved on its own first, because it is the contested part; the rest of the brief follows it.',
};

/** ADR-218 §2.3.2: the five states a part or a stage can be in, read from GitHub. */
export const STATES = ['not yet', 'waiting for your review', 'passed', 'being built', 'done'] as const;
export type ChangeState = (typeof STATES)[number];

/** The four parts of the brief (point 2), in reading order. */
export const PARTS = ['decision', 'contract', 'behaviour', 'plan'] as const;
export type PartKey = (typeof PARTS)[number];

export const PART_LABEL: Record<PartKey, string> = {
  decision: 'The decision',
  contract: 'The contract',
  behaviour: 'The behaviour',
  plan: 'The plan',
};

export const PART_WHAT: Record<PartKey, string> = {
  decision: 'The ADR or the amendment in the decisions repository: what changes, and why.',
  contract: 'openapi.yaml, asyncapi.yaml and contract.md in specs/changes/<slug>/, hand-written. The served contract changes only in the build.',
  behaviour: 'The .feature files. Every scenario is @proposed until the build flips it to @built; the wording is never edited in a build.',
  plan: 'plan.md in the same folder: the stories, one pull request in one repository each, the scenarios each flips, the release order. The board is populated from it on approval one, never before.',
};

export interface PullRequest {
  number: number;
  title: string;
  state: 'open' | 'merged';
  /** The branch it merges into: preprod, or the default branch. */
  base: string;
  /** owner/name */
  repo: string;
  openedAt: string;
  mergedAt: string | null;
  url: string;
  /** A release pull request's newest approving review on its head; null while it waits. */
  approvingReview: { by: string; at: string } | null;
}

/** A file the row follows: its document in the index and the pull request behind it. */
export interface TrackedFile {
  path: string;
  docId: string;
  pullRequest: PullRequest | null;
  /** Its last commit belongs to no pull request; it counts as merged. */
  direct: boolean;
}

export type ScenarioState = 'proposed' | 'built' | 'defect' | 'unbuilt' | 'open' | '';

export interface Scenario {
  /** Empty for a scenario without one. */
  id: string;
  title: string;
  state: ScenarioState;
  steps: string[];
}

export interface ScenarioCounts {
  proposed: number;
  built: number;
  defect: number;
  unbuilt: number;
  open: number;
  untagged: number;
  total: number;
}

export interface FeatureFile extends TrackedFile {
  /** The Feature: line. */
  title: string;
  runner: string;
  scenarios: Scenario[];
  counts: ScenarioCounts;
}

export interface Decision {
  adr: string;
  /** Empty when no connected repository holds the record. */
  title: string;
  status: string;
  docId: string;
  pullRequest: PullRequest | null;
}

export interface ContractOperation {
  method: string;
  path: string;
  operationId: string;
  summary: string;
}

export interface ContractChannel {
  name: string;
  address: string;
  messages: string[];
}

export interface Board {
  number: number;
  title: string;
  url: string;
  items: { todo: number; inProgress: number; done: number; truncated: boolean };
}

export interface ChangeStates {
  /** Null when the proposal names no decision: a small change has no such part. */
  decision: ChangeState | null;
  contract: ChangeState;
  behaviour: ChangeState;
  plan: ChangeState;
  release: ChangeState;
}

/** One row of GET /changes. */
export interface ChangeSummary {
  key: string;
  owner: string;
  repo: string;
  slug: string;
  title: string;
  size: ChangeSize;
  archived: boolean;
  states: ChangeStates;
  counts: ScenarioCounts;
  /** The open pull requests of the brief and the unapproved release, newest first. */
  waitingForReview: PullRequest[];
  lastActivity: string;
  syncedAt: string;
}

export interface ChangeList {
  items: ChangeSummary[];
  total: number;
  offset: number;
  limit: number;
}

/** GET /changes/{owner}/{repo}/{slug}. */
export interface Change extends ChangeSummary {
  proposal: TrackedFile;
  decision: Decision | null;
  contract: { files: TrackedFile[]; operations: ContractOperation[]; channels: ContractChannel[] };
  behaviour: { files: FeatureFile[]; counts: ScenarioCounts };
  plan: { file: TrackedFile | null; draftId: string | null; board: Board | null };
  release: { prs: PullRequest[] };
}

/* ───────────────────────── derivations ───────────────────────── */

export function changePath(change: Pick<ChangeSummary, 'owner' | 'repo' | 'slug'>): string {
  return `/changes/${change.owner}/${change.repo}/${change.slug}`;
}

export function repoFullName(change: Pick<ChangeSummary, 'owner' | 'repo'>): string {
  return `${change.owner}/${change.repo}`;
}

/** The parts a change of this size carries (point 5; point 4 gives every change a board). */
export function requiredParts(size: ChangeSize): PartKey[] {
  return size === 'small' ? ['contract', 'behaviour', 'plan'] : [...PARTS];
}

type WithStates = Pick<ChangeSummary, 'states' | 'archived'>;

/** Shape, from the brief's parts (contract.md §6): any part waiting → waiting;
 *  every part read past its approval → passed; else not yet. */
export function shapeState({ states }: WithStates): ChangeState {
  const parts = (['decision', 'contract', 'behaviour', 'plan'] as const)
    .map((part) => states[part])
    .filter((state): state is ChangeState => state !== null);
  if (parts.includes('waiting for your review')) return 'waiting for your review';
  return parts.includes('not yet') ? 'not yet' : 'passed';
}

/** Build, from the plan: its board being worked through, or all done. */
export function buildState({ states }: WithStates): ChangeState {
  return states.plan === 'being built' || states.plan === 'done' ? states.plan : 'not yet';
}

/** Learn is done when the change is archived. */
export function learnState({ archived }: WithStates): ChangeState {
  return archived ? 'done' : 'not yet';
}

export function stageStates(change: WithStates): Record<Stage, ChangeState> {
  return {
    shape: shapeState(change),
    build: buildState(change),
    release: change.states.release,
    learn: learnState(change),
  };
}

/** The stage is read from the states, never stored. */
export function stageOf(change: WithStates): Stage {
  if (change.archived) return 'learn';
  if (shapeState(change) !== 'passed') return 'shape';
  return change.states.release === 'not yet' ? 'build' : 'release';
}

export function countWaiting(change: Pick<ChangeSummary, 'waitingForReview'>): number {
  return change.waitingForReview.length;
}

export interface ReviewItem {
  change: ChangeSummary;
  pr: PullRequest;
}

/** What waits for the owner: one row per open pull request of each change. */
export function waitingForYourReview(changes: ChangeSummary[]): ReviewItem[] {
  return changes.flatMap((change) => change.waitingForReview.map((pr) => ({ change, pr })));
}

/** The label of a scenario's state, as the feature file tags it. */
export function scenarioTag(state: ScenarioState): string {
  return state ? `@${state}` : 'untagged';
}

/** "6 built · 3 proposed", in the order a reader asks, the zero counts left out. */
export function countsLine(counts: ScenarioCounts): string {
  const words: [keyof ScenarioCounts, string][] = [
    ['built', 'built'], ['proposed', 'proposed'], ['defect', 'defect'],
    ['unbuilt', 'unbuilt'], ['open', 'open'], ['untagged', 'untagged'],
  ];
  return words.filter(([key]) => counts[key] > 0).map(([key, word]) => `${counts[key]} ${word}`).join(' · ');
}

/** The distinct pull requests behind these files, an open one first: the thing to read. */
export function pullRequestsOf(files: TrackedFile[]): PullRequest[] {
  const found = new Map<string, PullRequest>();
  for (const file of files) {
    if (file.pullRequest) found.set(`${file.pullRequest.repo}#${file.pullRequest.number}`, file.pullRequest);
  }
  return [...found.values()].sort((a, b) => Number(b.state === 'open') - Number(a.state === 'open'));
}

/** A file as the repository holds it: the pull request's files while it is open. */
export function fileUrl(change: Pick<ChangeSummary, 'owner' | 'repo'>, file: TrackedFile): string {
  if (file.pullRequest?.state === 'open') return `${file.pullRequest.url}/files`;
  const path = file.path.split('/').map(encodeURIComponent).join('/');
  return `https://github.com/${repoFullName(change)}/blob/HEAD/${path}`;
}

/**
 * The summary block of a decision record: the blockquote holding **Summary.**
 * (the Documentation Standard's opening), else the first paragraph under the
 * first section. Empty when the record has neither.
 */
export function decisionSummary(markdown: string): string {
  const lines = markdown.split(/\r?\n/);
  const at = lines.findIndex((line) => /^>\s*\*\*Summary\.?\*\*/.test(line));
  if (at >= 0) {
    const quote: string[] = [];
    for (const line of lines.slice(at)) {
      if (!line.startsWith('>')) break;
      quote.push(line.replace(/^>\s?/, ''));
    }
    return quote.join('\n').trim();
  }
  const section = lines.findIndex((line) => line.startsWith('## '));
  if (section < 0) return '';
  const paragraph: string[] = [];
  for (const line of lines.slice(section + 1)) {
    if (line.trim()) paragraph.push(line);
    else if (paragraph.length) break;
  }
  return paragraph.join('\n').trim();
}

/** A document without its first `#` heading: the part that holds it is its title. */
export function withoutTitle(markdown: string): string {
  return markdown.replace(/^\s*# .*(?:\r?\n)+/, '');
}

/**
 * The lines of plan.md's `## What a customer will see` (contract.md §6), one per
 * sentence or bullet; empty when the plan has no such section.
 */
export function whatACustomerWillSee(plan: string): string[] {
  const lines = plan.split(/\r?\n/);
  const at = lines.findIndex((line) => /^##\s+What a customer will see\s*$/i.test(line));
  if (at < 0) return [];
  const body: string[] = [];
  for (const line of lines.slice(at + 1)) {
    if (line.startsWith('## ')) break;
    if (line.trim()) body.push(line.trim().replace(/^[-*]\s+/, ''));
  }
  return body;
}

/** "20 Oct 2026" — the page prints dates the way the owner reads them, once. */
export function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

export function formatMoment(iso: string): string {
  const d = new Date(iso);
  const day = formatDay(iso);
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${day}, ${hh}:${mm} UTC`;
}
